/**
 * lib/offlineQueueV1Migration.js — Founder correction (2026-09-09):
 * "Retiring lib/offlineQueue.js must not abandon pending operations
 * stored by the previous release."
 *
 * v1 (the deleted lib/offlineQueue.js) persisted pin-drop attempts to
 * IndexedDB (`fop_offline` → `pin_queue`, keyed by `localId`), shape
 * `{localId, sheetId, payload, queuedAt}`. Critically, v1's schema had
 * NO owner/tenant/user field at all — it was never identity-scoped.
 * That means any v1 entry found on this device is, by construction,
 * AMBIGUOUS: there is no way to know which user queued it. Per the
 * Founder's explicit instruction, this migration does NOT silently
 * assign that work to whichever user happens to be logged in when it
 * runs — it quarantines it for an explicit human recovery decision.
 *
 * Resumable + idempotent: an entry is only removed from the v1 store
 * AFTER it has been durably written into the quarantine list. If the
 * browser closes mid-migration, the next startup picks up exactly the
 * entries still remaining in v1 — nothing is lost, nothing is
 * double-quarantined (each entry is removed from source the moment
 * it's confirmed copied, so a repeat run sees a strictly smaller or
 * equal v1 store, never re-processes an already-migrated entry).
 */
const V1_DB_NAME = "fop_offline";
const V1_STORE = "pin_queue";
const DONE_FLAG_KEY = "fop_v1_migration_done";
const QUARANTINE_KEY = "fop_v1_legacy_quarantine";

function _openV1() {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") { reject(new Error("no indexedDB")); return; }
    const req = indexedDB.open(V1_DB_NAME, 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(V1_STORE)) {
        db.createObjectStore(V1_STORE, { keyPath: "localId" });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function _readQuarantine() {
  try {
    const raw = window.localStorage.getItem(QUARANTINE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}

function _writeQuarantine(list) {
  try {
    window.localStorage.setItem(QUARANTINE_KEY, JSON.stringify(list));
    return true;
  } catch { return false; }
}

async function _getAllV1() {
  const db = await _openV1();
  return new Promise((resolve) => {
    try {
      const tx = db.transaction(V1_STORE, "readonly");
      const req = tx.objectStore(V1_STORE).getAll();
      req.onsuccess = () => resolve(req.result || []);
      req.onerror = () => resolve([]);
    } catch { resolve([]); }
  });
}

async function _deleteV1Entry(localId) {
  const db = await _openV1();
  return new Promise((resolve, reject) => {
    try {
      const tx = db.transaction(V1_STORE, "readwrite");
      tx.objectStore(V1_STORE).delete(localId);
      tx.oncomplete = () => resolve(true);
      tx.onerror = () => reject(tx.error);
    } catch (e) { reject(e); }
  });
}

/** Run once per browser (idempotent — safe to call on every app start).
 * Never assigns legacy work to the current user; only quarantines it
 * for an explicit human decision via OfflineQueuePanel. */
export async function runV1MigrationOnce() {
  if (typeof window === "undefined") return;
  try {
    if (window.localStorage.getItem(DONE_FLAG_KEY) === "1") return;
  } catch { return; }

  let entries = [];
  try {
    entries = await _getAllV1();
  } catch {
    // IndexedDB unavailable (private mode, unsupported) — there is
    // nothing this migration can do; do not mark "done" so a future
    // session with IndexedDB available still gets a chance to check.
    return;
  }

  if (entries.length === 0) {
    try { window.localStorage.setItem(DONE_FLAG_KEY, "1"); } catch {}
    return;
  }

  const quarantine = _readQuarantine();
  const alreadyQuarantined = new Set(quarantine.map((q) => q.legacy_local_id));

  for (const entry of entries) {
    if (!alreadyQuarantined.has(entry.localId)) {
      quarantine.push({
        legacy_local_id: entry.localId,
        sheet_id: entry.sheetId,
        payload: entry.payload,
        queued_at: entry.queuedAt,
        discovered_at: new Date().toISOString(),
      });
      const persisted = _writeQuarantine(quarantine);
      if (!persisted) {
        // Can't write quarantine right now (storage full) — leave this
        // entry (and any after it) in v1 untouched; retry next startup.
        return;
      }
    }
    // Only delete from v1 AFTER the quarantine write above succeeded —
    // this is what makes an interrupted migration resumable without
    // ever losing an entry.
    try { await _deleteV1Entry(entry.localId); } catch { return; }
  }

  try { window.localStorage.setItem(DONE_FLAG_KEY, "1"); } catch {}
}

export function listQuarantinedLegacyPins() {
  return _readQuarantine();
}

/** A human on THIS device explicitly claims a quarantined legacy pin
 * as their own — the only path that ever attaches an owner to
 * ambiguous v1 work, and it requires a deliberate action, not an
 * automatic login-time assignment. Re-queues it into the canonical v2
 * queue under the CURRENTLY logged-in identity. */
export async function claimQuarantinedPin(legacyLocalId, offlineEnqueueFn) {
  const list = _readQuarantine();
  const item = list.find((q) => q.legacy_local_id === legacyLocalId);
  if (!item) return null;
  offlineEnqueueFn("pin.create", {
    sheetId: item.sheet_id,
    body: { ...item.payload },
  });
  _writeQuarantine(list.filter((q) => q.legacy_local_id !== legacyLocalId));
  return item;
}

export function discardQuarantinedPin(legacyLocalId) {
  const list = _readQuarantine();
  _writeQuarantine(list.filter((q) => q.legacy_local_id !== legacyLocalId));
}
